import React from 'react';
import { Loading, CTMModal } from '~/components';
import {
    config,
    parseQueryString,
    getError,
    getProfileToken,
    getToken,
    getAuthenticatedLocationHash,
    ErrorCode,
    getAuthCurrentLang,
    getLoginRedirect,
    setLoginRedirect,
    combineUrl,
    getCacheUserInfo,
    CTMPortalSystemType,
    getCurrentLang,
    getCacheMFAState,
    setCacheMFAState
} from '~/common';
import { loginLightningBySSO, validResetPasswordEmailToken, loginLightningByAccessToken, checkAuthMode } from '~/client';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Modal } from 'antd';
import { getLoginProfileSuccess } from '~/redux/loginProfile';
import { formatMessage as customFormatMessage } from '~/locales';

const UniqueRefType = {
    PretripApproval: 0,
    TripProposal: 1,
}

class AuthCallback extends React.Component {
    constructor(props) {
        super(props);
        this.currentLang = getCurrentLang();
    }

    componentDidMount() {
        this.mounted = true;
        this.queryObj = parseQueryString();

        const localUrl = document.location.href;
        if (localUrl.indexOf('/?') >= 0 && localUrl.indexOf('?') != localUrl.lastIndexOf('?')) {
            document.location.href = localUrl.replace('/?', '&');
        }
        if (this.queryObj['sso'] === '1') {
            this.handleSSOCallback();
        } else if (this.queryObj['reset-pwd'] === '1') {
            this.handleResetPassword();
        } else if (this.queryObj['token']) {
            this.handleLoginLightningByAccessToken(this.queryObj['token']);
        } else if (this.queryObj['mfaContext']) {
            this.handleCheckAuthMode({ mfaContext: this.queryObj['mfaContext'] })
        } else {
            this.handleAuthentication();
        }
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    handleSSOCallback = () => {
        if (this.queryObj['status'] === '-1') {
            let msg = this.queryObj['msg'] + (this.queryObj['errorCode'] ? ` (${this.queryObj['errorCode']})` : '');
            const authTimeout = this.queryObj['errorCode'] === ErrorCode.CheckIdTokenFail;
            if (authTimeout) {
                msg = this.props.intl.formatMessage({ id: 'timeout_tip' });
            }
            Modal.error({
                centered: true,
                title: this.props.intl.formatMessage({ id: 'system_information' }),
                content: msg,
                onOk: () => this.props.history.replace(authTimeout ? '/logout' : '/'),
            });
        } else {
            this.props.history.replace('/');
        }
    }

    handleAuthentication = () => {
        const { auth, history, location } = this.props;
        //redirect to home page case already authenticated
        const authenticatedHash = getAuthenticatedLocationHash();
        if (authenticatedHash && authenticatedHash === location.hash && auth.isAuthenticated() && getProfileToken()) {
            history.replace('/');
            return;
        }
        this.queryObj = parseQueryString();
        if (/access_token|id_token|error/.test(location.hash) || this.queryObj['code']) {
            auth.handleAuthentication(this.handleCheckAuthMode);
        }
    }

    handleCheckAuthMode = (param) => {
        const { email, extContextId, route, authContext, mfaContext, isAutoLogin, autoLoginUrl } = param;
        if (isAutoLogin) {//reset password auto login
            if (autoLoginUrl) {
                window.location.href = autoLoginUrl;
            } else {
                this.handleLoginLightningBySSO({ email, extContextId, route });
            }
            return;
        }
        const { history } = this.props;
        const params = {
            Token: config.obtApiToken,
            AccessToken: getToken(),
            SystemType: CTMPortalSystemType,
            AuthContext: authContext,
            AppId: config.authClientID,
            MFAContext: mfaContext,
            CallBackUrl: combineUrl(config.localhostUrl, `/callback`),
            GoBackUrl: combineUrl(config.localhostUrl, `/logout`),
        };
        checkAuthMode(params, (err, rs) => {
            const errMsg = getError(rs);
            if (errMsg) {
                Modal.error({
                    centered: true,
                    title: this.props.intl.formatMessage({ id: 'system_information' }),
                    content: errMsg,
                    onOk: () => history.replace('/logout'),
                });
                return;
            }
            if (rs.EnableMFA) {
                if (rs.RedirectUrl) {
                    window.location.href = rs.RedirectUrl;
                }
                else if (rs.IsMFAVerified) {
                    this.handleLoginLightningBySSO({ email: rs.Email, extContextId, route, aid: rs.AuthId });
                }
            } else {
                this.handleLoginLightningBySSO({ email, extContextId, route, authContext, aid: rs.AuthId });
            }
        });
    }


    handleLoginLightningBySSO = ({ email, extContextId, route, authContext, aid }) => {
        const { history, auth, getLoginProfileSuccess } = this.props;
        let params = {
            Token: config.obtApiToken,
            AccessToken: getToken(),
            Email: email,
            ContextId: extContextId,
            SystemType: CTMPortalSystemType,
            AppId: config.authClientID,
            AuthContext: authContext,
        };
        loginLightningBySSO(params, (err, data) => {
            if (data && data.ProfileToken) {
                auth.scheduleRenewal(data.CheckSessionInterval);
                getLoginProfileSuccess(data);
                const redirectUrl = getLoginRedirect();
                if (redirectUrl) {
                    setLoginRedirect('');
                    window.location.href = redirectUrl;
                    return;
                }
                if (this.currentLang && this.currentLang !== data.UserSetting.Language) {
                    window.location.href = combineUrl(config.localhostUrl, route);
                } else {
                    history.replace(route || '/');
                }
            } else {
                // auth.clearData(false);
                config.isDebug && console.log("debug mode message:", err, data);
                Modal.error({
                    centered: true,
                    title: this.props.intl.formatMessage({ id: 'system_information' }),
                    content: getError(data) || this.props.intl.formatMessage({ id: 'login_lightning_failed_prompt' }),
                    onOk: () => history.replace('/logout'),
                });
            }
        }, aid);
    }

    handleLoginLightningByAccessToken = (accessToken) => {
        const { history, auth, getLoginProfileSuccess } = this.props;
        loginLightningByAccessToken({ accessToken, systemType: CTMPortalSystemType }, (err, data) => {
            if (data && data.ProfileToken) {
                const currentLogin = getCacheUserInfo();
                let reload = this.currentLang && this.currentLang !== data.UserSetting.Language;
                if (auth.isAuthenticated() && currentLogin && currentLogin.loginId != data.LoginId) {
                    auth.clearData(false);
                    reload = true;
                }
                data.EmailToken = accessToken;
                getLoginProfileSuccess(data);
                let route = '/';
                if (data.RedictData) {
                    route = `/pre-trip/detail?requestRef=${data.RedictData.UniqueRef}${data.RedictData.Action ? `&action=${data.RedictData.Action}` : ''}`;
                    if (data.RedictData.UniqueRefType == UniqueRefType.TripProposal) {
                        route = `/trip-proposal/detail?referenceNumber=${data.RedictData.UniqueRef}${data.RedictData.Action ? `&action=${data.RedictData.Action}` : ''}${this.queryObj['isPreview'] ? `&isPreview=${this.queryObj['isPreview']}` : ''}`;
                    }
                }
                if (reload) {
                    window.location.href = combineUrl(config.localhostUrl, route);
                } else {
                    history.replace(route);
                }
            } else {
                auth.clearData(false);
                config.isDebug && console.log("debug mode message:", err, data);
                CTMModal.error({
                    centered: true,
                    title: this.props.intl.formatMessage({ id: 'system_information' }),
                    content: getError(data) || this.props.intl.formatMessage({ id: 'login_lightning_failed_prompt' }),
                    onOk: () => history.replace('/logout'),
                });
            }
        });
    }

    handleResetPassword = () => {
        const token = this.queryObj['token'];
        const params = {
            Token: config.obtApiToken,
            EmailToken: token,
            MsgLanguage: getAuthCurrentLang(),
            SystemType: CTMPortalSystemType,
            MFACallBackUrl: combineUrl(config.localhostUrl, 'callback' + window.location.search),
            MFAGoBackUrl: combineUrl(config.localhostUrl, 'logout'),
            MFAContext: this.queryObj['mfaContext'],
        };
        const fm = id => customFormatMessage({ id, lang: getAuthCurrentLang() });
        if (!token) {
            Modal.error({
                centered: true,
                title: fm('system_information'),
                okText: fm('ok'),
                content: fm('invalid_parameter'),
                onOk: () => this.props.history.replace('/'),
            });
            return;
        }
        if (params.MFAContext) {
            const validToken = getCacheMFAState();
            if (validToken !== token) {
                console.error('email token state not found', { validToken, token });
                Modal.error({
                    centered: true,
                    title: fm('system_information'),
                    okText: fm('ok'),
                    content: fm('invalid_parameter'),
                    onOk: () => this.props.history.replace('/'),
                });
                return;
            }
        }
        validResetPasswordEmailToken(params, (err, data) => {
            const errMsg = getError(err || data);
            if (errMsg) {
                Modal.error({
                    centered: true,
                    title: fm('system_information'),
                    okText: fm('ok'),
                    content: errMsg,
                    onOk: () => this.props.history.replace('/'),
                });
            } else {
                if (data.MFARedirectUrl) {
                    setCacheMFAState(token);
                    window.location.href = data.MFARedirectUrl;
                    return;
                }
                setCacheMFAState('');
                this.props.history.replace(`${config.resetPasswordRoute}?token=${encodeURIComponent(token)}`, data.Email);
            }
        });
    }

    render() {
        return <Loading style={{ backgroundColor: 'rgba(0,0,0,.85)' }} />;
    }
}

export default connect(null, dispatch => ({
    getLoginProfileSuccess: payload => dispatch(getLoginProfileSuccess(payload)),
}))(injectIntl(AuthCallback));
